// FUB API Service for fetching agents/users
export interface FUBUser {
  id: number
  firstName: string
  lastName: string
  email: string
  role: string
  teamLeaderOf?: number[]
}

export interface FUBPerson {
  id: number
  firstName: string
  lastName: string
  email: string
  assignedUserId?: number
  // Add other person fields as needed
}

export interface FUBResponse<T> {
  _metadata: {
    collection: string
    offset: number
    limit: number
    total: number
    next?: string
    nextLink?: string
  }
  users?: T[]
  people?: T[]
}

class FUBApiService {
  private baseUrl = 'https://api.followupboss.com/v1'

  private getApiKey(): string {
    // Access React environment variables
    const apiKey = process.env.REACT_APP_FUB_API_KEY
    console.log('FUB API Key check:', {
      hasKey: !!apiKey,
      keyLength: apiKey ? apiKey.length : 0,
      keyPreview: apiKey ? `${apiKey.substring(0, 4)}...` : 'none'
    })
    
    if (!apiKey) {
      throw new Error('FUB API key not configured')
    }
    
    return apiKey
  }

  private getAuthHeaders(): HeadersInit {
    const apiKey = this.getApiKey()
    
    // Basic Authentication as per FUB docs
    // Format: API_KEY: (empty password)
    const credentials = btoa(`${apiKey}:`)
    console.log('FUB Auth headers:', {
      hasApiKey: !!apiKey,
      credentialsLength: credentials.length,
      authHeader: `Basic ${credentials.substring(0, 10)}...`
    })
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Basic ${credentials}`,
    }
  }

  async fetchPerson(personId: number): Promise<FUBPerson | null> {
    try {
      const url = `${this.baseUrl}/people/${personId}`
      console.log('FUB Fetch Person:', { url, personId })

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (!response.ok) {
        if (response.status === 404) {
          console.log('Person not found:', personId)
          return null
        }
        throw new Error(`API error: ${response.status}`);
      }

      const person: FUBPerson = await response.json()
      console.log('FUB Person Response:', { 
        personId: person.id,
        assignedUserId: person.assignedUserId,
        name: `${person.firstName} ${person.lastName}`
      })
      
      return person
    } catch (error) {
      console.error('Error fetching FUB person:', error)
      return null
    }
  }

  async fetchAllUsers(): Promise<FUBUser[]> {
    const people: FUBUser[] = []
    let nextToken: string | null = null

    try {
      while (true) {
        // Build URL with nextToken if present
        const url: string = `${this.baseUrl}/users?limit=100` + 
                   (nextToken ? `&next=${encodeURIComponent(nextToken)}` : "")

        console.log('FUB API Request:', { url, hasNextToken: !!nextToken })

        // Call the API directly
        const response = await fetch(url, {
          method: "GET",
          headers: this.getAuthHeaders(),
        })

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json()
        people.push(...data.users)

        console.log('FUB API Response:', { 
          usersInBatch: data.users?.length || 0,
          totalUsersSoFar: people.length,
          hasNext: !!data._metadata?.next
        })

        // Stop if there's no more paging token
        if (!data._metadata?.next) {
          break;
        }
        nextToken = data._metadata.next
      }

      console.log('FUB Total Users Fetched:', people.length)
      return people
    } catch (error) {
      console.error('Error fetching all FUB users:', error)
      throw error
    }
  }

  async fetchAgents(): Promise<FUBUser[]> {
    try {
      const allUsers = await this.fetchAllUsers()
      
      // Include all users - no filtering by role
      const sortedUsers = allUsers.sort((a, b) => {
        const firstNameA = a.firstName.toLowerCase()
        const firstNameB = b.firstName.toLowerCase()
        return firstNameA.localeCompare(firstNameB)
      })

      return sortedUsers
    } catch (error) {
      console.error('Error fetching FUB users:', error)
      throw error
    }
  }

  async getDefaultAgent(personId?: number): Promise<FUBUser | null> {
    try {
      const agents = await this.fetchAgents()
      
      if (agents.length === 0) {
        return null
      }

      // If we have a person ID, try to get their assigned user
      if (personId) {
        const person = await this.fetchPerson(personId)
        if (person?.assignedUserId) {
          const assignedAgent = agents.find(agent => agent.id === person.assignedUserId)
          if (assignedAgent) {
            return assignedAgent
          }
        }
      }

      // Don't fall back to any agent - leave empty if no assigned user found
      return null
    } catch (error) {
      console.error('Error getting default agent:', error)
      return null
    }
  }
}

export default new FUBApiService()
